import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useEffect, useState } from "react"
import { api } from "../../api/client"
import { useStore } from "../../store/useStore"
import type { PrimitiveDef } from "../../types"
import { getPrimitiveDisplayTitle } from "../config/scopeIcons"

export function RecordForm() {
  const { translate } = useTranslation()
  const { editRecordId, activeScopeId, closeEdit } = useStore()
  const qc = useQueryClient()

  const isNew = editRecordId === "new"

  // Scope config
  const { data: pageData } = useQuery({
    queryKey: ["records", activeScopeId],
    queryFn: () => api.listRecords(activeScopeId),
  })

  // Existing record data when editing
  const existingRecord =
    !isNew && pageData
      ? pageData.records.find((r) => r.id === editRecordId)
      : undefined

  const scope = pageData?.scope
  const editablePrims =
    scope?.primitives.filter(
      (p) => p.type !== "relation" && p.type !== "date" && !p.config?.hidden,
    ) ?? []
  const relationPrims =
    scope?.primitives.filter((p) => p.type === "relation") ?? []

  // Field state
  const [values, setValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  // Initialise from existing record when editing
  useEffect(() => {
    if (!isNew && existingRecord) {
      const init: Record<string, string> = {}
      editablePrims.forEach((p) => {
        init[p.name] = existingRecord.values[p.name]?.value ?? ""
      })
      setValues(init)
    } else if (isNew) {
      setValues({})
    }
  }, [editRecordId, existingRecord])

  if (!editRecordId || !scope) return null

  const validate = () => {
    const errs: Record<string, string> = {}
    editablePrims.forEach((p) => {
      if (p.is_pk && !values[p.name]?.trim()) {
        errs[p.name] = translate("QMS/CRUD/REQUIRED_PK")
      }
      if (
        p.type === "number" &&
        values[p.name] &&
        isNaN(Number(values[p.name]))
      ) {
        errs[p.name] = translate("QMS/CRUD/MUST_BE_NUMBER")
      }
    })
    return errs
  }

  const handleSave = async () => {
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setSaving(true)
    try {
      const payload: Record<
        string,
        { value: string; source: string; confirmed_by_user: boolean }
      > = {}
      editablePrims.forEach((p) => {
        payload[p.name] = {
          value: values[p.name] ?? "",
          source: "manual",
          confirmed_by_user: true,
        }
      })

      if (isNew) {
        await api.createRecord(activeScopeId, { values: payload })
      } else {
        await api.updateRecord(editRecordId!, { values: payload })
      }
      qc.invalidateQueries({ queryKey: ["records", activeScopeId] })
      closeEdit()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center"
      onClick={closeEdit}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-xs" />
      <div
        className="relative mx-4 w-full max-w-lg overflow-hidden rounded-lg border border-grey4 bg-grey2 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-grey4 bg-grey1 px-6 py-4">
          <h3 className="font-semibold text-white">
            {isNew
              ? translate("QMS/CRUD/CREATE_SCOPE", { scope: scope.name })
              : translate("QMS/CRUD/EDIT_SCOPE", { scope: scope.name })}
          </h3>
          <button
            onClick={closeEdit}
            className="text-xl text-grey5 hover:text-white"
          >
            ×
          </button>
        </div>

        {/* Fields */}
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-6 py-5">
          {editablePrims.map((p) => (
            <FieldRow
              key={p.id}
              prim={p}
              value={values[p.name] ?? ""}
              error={errors[p.name]}
              onChange={(v) => {
                setValues((prev) => ({ ...prev, [p.name]: v }))
                setErrors((prev) => ({ ...prev, [p.name]: "" }))
              }}
            />
          ))}

          {/* Relation fields (display only for now — full multi-select is Phase 1b+) */}
          {relationPrims.length > 0 && (
            <div className="border-t border-grey4 pt-2">
              <p className="text-xs text-grey5">
                {translate("QMS/CRUD/RELATIONS_HELP")}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-grey4 bg-grey1 px-6 py-4">
          <button
            onClick={closeEdit}
            className="px-4 py-2 text-sm text-grey5 hover:text-white"
          >
            {translate("QMS/COMMON/CANCEL")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow3 disabled:opacity-50"
          >
            {saving
              ? translate("QMS/SUPPLIER/SAVING")
              : isNew
                ? translate("QMS/CRUD/CREATE")
                : translate("QMS/COMMON/SAVE")}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Field Row
// ---------------------------------------------------------------------------

interface FieldRowProps {
  prim: PrimitiveDef
  value: string
  error?: string
  onChange: (v: string) => void
}

function FieldRow({ prim, value, error, onChange }: FieldRowProps) {
  const { translate, currentLanguage } = useTranslation()
  const displayTitle = getPrimitiveDisplayTitle(prim, currentLanguage)
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-grey6">
        {displayTitle}
        {prim.is_pk && (
          <span
            className="ml-1 text-xs text-warning"
            title={translate("QMS/CRUD/PK_TOOLTIP")}
          >
            PK
          </span>
        )}
        {prim.config?.mapped_to && (
          <span
            className="ml-1 text-xs text-yellow1"
            title={translate("QMS/CRUD/MAPS_TO_TOOLTIP", {
              target: prim.config.mapped_to,
            })}
          >
            ⇄ {prim.config.mapped_to}
          </span>
        )}
      </label>
      {prim.description && (
        <p className="mb-1 text-xs text-grey5">{prim.description}</p>
      )}
      <input
        type={prim.type === "number" ? "text" : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          prim.type === "number"
            ? "0.0"
            : translate("QMS/CRUD/FIELD_PLACEHOLDER", { title: displayTitle })
        }
        className={clsx(
          "w-full rounded-lg border bg-grey3 px-3 py-2 text-sm text-white placeholder:text-grey5 focus:outline-hidden focus:ring-1",
          error
            ? "border-red1 focus:ring-red1"
            : "border-grey4 focus:border-yellow1 focus:ring-yellow1",
        )}
      />
      {error && <p className="mt-1 text-xs text-red1">{error}</p>}
    </div>
  )
}
